import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from "react";
import { type Product, getEffectivePrice } from "@/hooks/useProducts";

export interface CartItem {
  product: Product;
  quantity: number;
  variantLabel?: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (product: Product, quantity?: number, variantLabel?: string) => void;
  removeItem: (productId: number, variantLabel?: string) => void;
  updateQuantity: (productId: number, quantity: number, variantLabel?: string) => void;
  clearCart: () => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  totalItems: number;
  subtotal: number;
}

const CartContext = createContext<CartContextType | null>(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};

const itemKey = (id: number, variant?: string) => `${id}::${variant || ""}`;

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem("plym_cart");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  // Persist cart to localStorage whenever items change
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    localStorage.setItem("plym_cart", JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product: Product, quantity = 1, variantLabel?: string) => {
    setItems((prev) => {
      const key = itemKey(product.id, variantLabel);
      const existing = prev.find((i) => itemKey(i.product.id, i.variantLabel) === key);
      if (existing) {
        return prev.map((i) =>
          itemKey(i.product.id, i.variantLabel) === key
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      return [...prev, { product, quantity, variantLabel }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((productId: number, variantLabel?: string) => {
    setItems((prev) => prev.filter((i) => itemKey(i.product.id, i.variantLabel) !== itemKey(productId, variantLabel)));
  }, []);

  const updateQuantity = useCallback((productId: number, quantity: number, variantLabel?: string) => {
    if (quantity <= 0) {
      removeItem(productId, variantLabel);
      return;
    }
    setItems((prev) =>
      prev.map((i) =>
        itemKey(i.product.id, i.variantLabel) === itemKey(productId, variantLabel)
          ? { ...i, quantity }
          : i
      )
    );
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem("plym_cart");
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => {
    const price = getEffectivePrice(i.product);
    return sum + price * i.quantity;
  }, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, isOpen, setIsOpen, totalItems, subtotal }}>
      {children}
    </CartContext.Provider>
  );
};
